<template>
  <!-- STEP 1 -->
  <main class="type-selection" id="mainContent">
    <Stepper :step="stepNum" />
    <div class="app-main-content">
      <h1>{{ screenData.title }}</h1>
      <p>{{ screenData.description }}</p>

      <div class="app-content-panels">
        <!-- left panel (form) -->
        <form class="app-panel-left">
            <!-- Screen 0: Category + Visit Type selection -->
            <div class="app-input-group" ref="visitCategoryRef">
              <p>Choose your primary reservation.</p>
              <p class="subtext">
                If you have an Adjudication or a CDL transaction, please make that your primary reservation
                in the drop-down below.
              </p>
              <label for="selectedCategory" class="sr-only">Select visit category</label>
              <select
                class="app-select"
                id="selectedCategory"
                v-model="myCat"
                @change="getTypesByCategory"
                :class="{ 'disabled': isModify }"
                :disabled="isModify"
                :title="isModify ? 'Reservation type cannot be changed. ' : ''"
              >
                <option value="-1">Select a category</option>
                <option
                  v-for="cat in filteredVisitCategories"
                  :value="cat.visitCategoryId"
                  :key="`cat-${cat.visitCategoryId}`"
                >
                  {{ cat.visitCategoryDescription }}
                </option>
              </select>
            </div>
            <transition
              mode="out-in"
              enter-active-class="animate__animated animate__fadeIn"
              leave-active-class="animate__animated animate__fadeOut"
            >
              <div v-if="showTypes" class="app-input-group" ref="visitType">
                <p>What type of reservation(s) do you need to make?</p>
                <div v-for="(type) in selectedTypes" :key="`type-${type.visitTypeId}`" class="type-class">
                  <label :for="`visitType${type.visitTypeId}`" :ref="`visitType${type.visitTypeId}Label`" class="visit-type">
                  <input
                    type="radio"
                    name="visitType"
                    :id="`visitType${type.visitTypeId}`"
                    :data-customer="type.allowedCustomerTypeId"
                    :data-fee="type.isFeeApplicable"
                    :data-id="type.visitTypeId"
                    :data-catid="type.visitCategoryId"
                    :value="type.visitTypeId"
                    v-model="myType"
                    @click="typeSelected"
                    :class="{ 'disabled': isModify }"
                    :disabled="isModify"
                  />
                  <span v-html="highlight(type.visitTypeDescription)" class="res-visit-type"></span>
                  
                  <span v-if="type.visitTypeDetailedDescription.length > 0">
                    <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']" offset="5" class="app-tool-tip">
                      <button type="button" class="btn-tooltip-trigger" aria-label="Visit type details">
                        <img
                          src="@ridmv/web-themes/images/icons/question-circle-solid.svg"
                          alt="Definition"
                          aria-hidden="true"
                        />
                      </button>
                      <span class="sr-only">{{ type.visitTypeDetailedDescription }}</span>
                      <template #content>
                        {{ type.visitTypeDetailedDescription}}
                      </template>
                      <template #popper>
                        {{ type.visitTypeDetailedDescription}}
                      </template>
                    </Tooltip>
                  </span>
                  </label>
                </div>
              </div>
            </transition>

          <BaseButtonGroup>
            <button type="button" @click="handleRouting(true)">{{ language.btnPrev }}</button>
            <button
              type="button"
              class="app-btn-primary"
              :class="{ 'disabled': !enableNext }"
              :aria-disabled="!enableNext"
              @click="handleRouting(false)"
            >
              {{ language.btnNext }}
            </button>
            
          </BaseButtonGroup>
        </form>

        <!-- right panel -->
        <AsideForType />
      </div>
    </div>
    <!-- /app-main-content -->
  </main>
</template>


<script setup>
import { ref, computed, onBeforeMount, onMounted, watch, onUpdated } from 'vue';
import appConfig from '@/app.config';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import AsideForType from '../components/reservation/aside/AsideForType.vue'
import Stepper from '@/components/reservation/Stepper';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import { LOOKUP_SET_VISIT_DATA, LOOKUP_STORE_DATA } from '@/store/_commons';
import { Tooltip } from 'floating-vue';
import '@/assets/tooltip.css';
import { useHead } from '@unhead/vue';
import VISIT_TYPE_SUB_OPTIONS from '@/assets/data/_visitTypeSubOptions';

// usehead to manage the page meta
useHead({
  title: `Type of reservation | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Select the type of reservation per person to complete all your RI DMV business.'
    }
  ]
})

// Vuex Router and Store
const router = useRouter();
const route = useRoute();
const store = useStore();

// Reactive state
const stepNum = ref(1);
const screenData = ref({});
const selectedTypes = ref([]);
const showTypes = ref(false);
const myCat = ref(-1);
const myType = ref(-1);
const enableNext = ref(false);
const isModify = ref(false);

// Template refs
const visitCategoryRef = ref(null);

// Vuex state and getters replacements
const LANG = computed(() => store.state.user.LANG);
const STEPS = computed(() => store.state.user.STEPS);
const resCategory = computed(() => store.state.user.resCategory);
const resType = computed(() => store.state.user.resType);
const resSlot = computed(() => store.state.user.resSlot);
const resTypeForCustomer = computed(() => store.state.user.resTypeForCustomer);
const lookupStates = computed(() => store.getters[LOOKUP_STORE_DATA]);

const allVisitCategories = computed(() => lookupStates.value.allVisitCategories);
const allVisitTypes = computed(() => lookupStates.value.allVisitTypes);
const language = computed(() => LANG.value);
const PAGES = computed(() => store.state.user.PAGES);
const onlineToken = computed(() => store.state.user.onlineToken);

// OCA customer type — only populated when arriving via "Make a new Reservation" link
const ocaCustomerType = computed(() => {
  const queryType = route.query?.customerType?.trim();
  if (queryType) {
    return queryType;
  }
  return (store.state.user.ocaCustomerType ?? '');
});

const isIndividualOcaCustomer = computed(() => (ocaCustomerType.value ?? '') == 1);

const subOptionsConfig = computed(() => VISIT_TYPE_SUB_OPTIONS[myType.value]);

// Filter categories: individuals see all; non-individuals see only categories
// that contain types with allowedCustomerTypeId === 3 (BOTH)
const filteredVisitCategories = computed(() => {
  let categories;
  if (!onlineToken.value || (onlineToken.value && isIndividualOcaCustomer.value)) {
    categories = allVisitCategories.value;
  } else {
    const allowedCategoryIds = new Set(
      allVisitTypes.value
        .filter(t => t.allowedCustomerTypeId === 3)
        .map(t => t.visitCategoryId)
    );
    categories = allVisitCategories.value.filter(c => allowedCategoryIds.has(c.visitCategoryId));
  }

  // Deduplicate by visitCategoryId (API return duplicates for mixed-type categories)
  const existingId = new Set();
  return categories.filter(c => {
    if (existingId.has(c.visitCategoryId)) return false;
    existingId.add(c.visitCategoryId);
    return true;
  });
});

// Actions to dispatch
const setVisitData = () => store.dispatch(LOOKUP_SET_VISIT_DATA);

const setStoreProperty = (payload) => store.dispatch('user/setStoreProperty', payload);
const setReservationDetails = (payload) => store.dispatch('user/setReservationDetails', payload);

// Watchers
watch(myCat, (newCat) => {
  // first, reset type ID IF category is changed
  myType.value = (newCat === resCategory.value) ? myType.value : -1;
  // re-grab types if user returns to current screen, e.g. from next screen
  if (newCat > 0) {
    getTypesByCategory();
  }
  // update session storage
  setStoreProperty({ property: 'resCategory', value: newCat });
});

watch(myType, (newType) => {
  // reset [previously] selected slot when service type changes
  if (resSlot.value > -1) {
    setStoreProperty({ property: 'resSlot', value: -1 });
  }

  // a different type invalidates any prior sub-option selection for this flow
  setStoreProperty({ property: 'resSubStep', value: 0 });
  setStoreProperty({ property: 'resSubOption', value: null });
  
  setStoreProperty({ property: 'resType', value: newType });
  if (newType > 0) {
    // The original code used `this.$refs`.
    const selectedTypeObj = selectedTypes.value.find(type => type.visitTypeId === newType);
    if (selectedTypeObj) {
      setReservationDetails([{ property: 'serviceType', value: selectedTypeObj.visitTypeDescription }]);
    }
  }
  enableNextStep();
});

watch(selectedTypes, (newTypes) => {
  showTypes.value = newTypes.length > 0;
});

// Lifecycle hooks
onBeforeMount(() => {
  screenData.value = STEPS.value[stepNum.value];
  if (allVisitCategories.value.length > 0 && resCategory.value > 0 && resType.value > 0) {
    // user has already previously selected service type
    myCat.value = resCategory.value;
    myType.value = resType.value;
  } else {
    setVisitData();
  }
});

onMounted(async () => {
  enableNextStep();
  isModify.value = await store.dispatch('user/isReservationForUpdate');

  const queryOnlineToken = route.query.onlineToken;
  if (store.state.user.onlineToken == '') {
    if(queryOnlineToken != '') {
      store.state.user.onlineToken = queryOnlineToken;
    } 
  }

  // Persist customerType from URL into the store (for refresh/back navigation)
  const queryCustomerType = (route.query.customerType ?? '').trim();
  if (queryCustomerType && !store.state.user.ocaCustomerType) {
    store.state.user.ocaCustomerType = queryCustomerType;
  }
});

onUpdated(() => {
  enableNextStep();
});

// Methods
const enableNextStep = () => {
  enableNext.value = (myType.value > 0 && showTypes.value);
  if (myType.value === 35 || myType.value === 36 || myType.value === 37) {
    setStoreProperty({ property: 'ocaIsModify', value: true });
  }
};

const getTypesByCategory = () => {
  selectedTypes.value = allVisitTypes.value.filter(type => type.visitCategoryId === myCat.value);

  // Non-individual OCA users: only show types available to both customer types
  if (onlineToken.value && !isIndividualOcaCustomer.value) {
    selectedTypes.value = selectedTypes.value.filter(type => type.allowedCustomerTypeId === 3);
  }

  // data in db will not include "fee hint" so it must be added in frontend
  selectedTypes.value.forEach(item => {
    if (item.isFeeApplicable) {
      const hint = '(Fee required)';
      // Use includes() for a more readable check than indexOf()
      if (!item.visitTypeDescription.includes(hint)) {
        item.visitTypeDescription = `${item.visitTypeDescription} ${hint}`;
      }
    }
  });
};

const handleRouting = (isPrev = false) => {
  if (isPrev) {
    router.push({ name: PAGES.value.home });
    return;
  }
  if (!enableNext.value) return;
  if (subOptionsConfig.value && !onlineToken.value) {
    router.push({ name: PAGES.value.selectTypeOptions });
  } else {
    router.push({ name: PAGES.value.selectSlot });
  }
};

const highlight = (str) => {
  const leftParenthesis = str.indexOf('(');
  const rightParenthesis = str.indexOf(')');
  if (leftParenthesis > -1 && rightParenthesis > -1) {
    return `${str.substring(0, leftParenthesis)}<b>${str.substring(leftParenthesis, rightParenthesis + 1)}</b>${str.substr(rightParenthesis + 1)}`;
  }
  return str;
};

const typeSelected = (e) => {
  // do not depend on visitTypeId (myType) for applicable fee...
  // check isFeeApplicable for each type to avoid out-of-sync issue with backend
  const dataset = e.target.dataset;

  if (dataset) {
    const payUp = dataset.fee === 'true'; 

    setStoreProperty({ property: 'resFeeApplicable', value: payUp });

    const allowed = Number(dataset.customer) || 3;  // 3 - BOTH
    const typeId = Number(dataset.id);
    const categoryId = Number(dataset.catid);
    console.error("Constant")
    console.error(categoryId)
    console.error(typeId)
    setStoreProperty({
      property: 'resTypeForCustomer',
      value: {
        allowedCustomerTypeId: allowed,
        isFeeApplicable: payUp,
        visitTypeId: typeId,
        visitCategoryId: categoryId,
      }
    });
    console.error("Constant")
    console.error(resTypeForCustomer.value.categoryId)
    console.error(resTypeForCustomer.value.typeId)
  }
}
</script>


<style lang="scss" scoped>
@import "~animate.css";
.animate__animated.animate__fadeOut {
  --animate-duration: .35s;
}
.type-selection {
  select {
    width: unset;
  }
}
.type-class {
  display: block;
  label {
    display: inline;
    padding-left: .5em;
  }
  span {
    display: inline-block;
    img {
      margin-bottom: -2px;
      width: 16px;
      :focus-visible{
        color: orange !important;
      }
    }
  }
  .visit-type{
    padding-left: 0em !important;
  }
  .res-visit-type {
    margin-left: 0.5rem;
    font-weight: 400;
  }
  
  .app-tool-tip {
    display: inline-block;
    margin-left: .5em;
    cursor: pointer; 
    .btn-tooltip-trigger{
      min-width: 25px;
      border: none;
      background-color: unset;
      border-radius: unset;
      padding: 0rem;
    }
  }

}

.registration-oca-notice {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid var(--ri-blue, #003a70);
  background: #f0f4f8;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    img { width: 0.75rem; }
  }
}

.app-button-group {
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}

@media only screen and (max-width: 880px) {
  .type-class {
    display: flex;
    width: 100%;
    align-items: flex-start;
    input[type=radio] {
      margin-top: 0.5em;
    }

    .visit-type{
      padding-left: 0em !important;
      display: flex;
      align-items: baseline;
    }
    .res-visit-type {
      margin-left: 0.5rem;
    }
  }
}
</style>
